import React, { useEffect, useRef, useCallback } from 'react';
import { applyBrandingCssVars, getButtonBorderRadius, isWhitePrimary } from '../../utils/sidebarBrandingColors';

const PREVIEW_REAPPLY_DELAYS_MS = [0, 100, 300, 700, 1500];

const BrandingPreview = ({
  brandColor,
  sidebarColor,
  infoColor,
  successColor,
  warningColor,
  dangerColor,
  scooterColor,
  buttonStyle,
  fontFamily,
  logoUrl,
  companyName,
  companyWebsite,
  privacyPolicyUrl,
  termsAndConditionsUrl,
  height = 600,
}) => {
  const iframeRef = useRef(null);
  const reapplyTimersRef = useRef([]);

  const injectBranding = useCallback(() => {
    try {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      const familyRaw = (fontFamily || '').split(',')[0].replace(/['"]/g, '').trim();
      if (familyRaw) {
        const gfId = `gf-${familyRaw.replace(/\s+/g, '-')}`;
        if (!doc.getElementById(gfId)) {
          const link = doc.createElement('link');
          link.id = gfId;
          link.rel = 'stylesheet';
          const urlFamily = `${familyRaw.replace(/\s+/g, '+')}:wght@300;400;500;600;700`;
          link.href = `https://fonts.googleapis.com/css2?family=${urlFamily}&display=swap`;
          doc.head.appendChild(link);
        }
      }

      // Inject override styles
      const styleId = 'branding-preview-overrides';
      let styleTag = doc.getElementById(styleId);
      if (!styleTag) {
        styleTag = doc.createElement('style');
        styleTag.id = styleId;
        doc.head.appendChild(styleTag);
      }

      const color = brandColor || '#000a60';
      const sidebarBg = sidebarColor || color;
      const info = infoColor || '#2bcfe0';
      const success = successColor || '#2b9e62';
      const warning = warningColor || '#fb6340';
      const danger = dangerColor || '#ea2817';
      const scooter = scooterColor || '#2bcfe0';
      const buttonBorderRadius = getButtonBorderRadius(buttonStyle);
      const family = fontFamily || '"Nunito Sans", sans-serif';
      const whitePrimary = isWhitePrimary(color);

      applyBrandingCssVars(doc.documentElement, {
        themeColor: color,
        sidebarColor: sidebarBg,
        infoColor: info,
        successColor: success,
        warningColor: warning,
        dangerColor: danger,
        scooterColor: scooter,
        buttonStyle,
      });

      const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        if (h.length === 3) {
          const r = parseInt(h[0] + h[0], 16);
          const g = parseInt(h[1] + h[1], 16);
          const b = parseInt(h[2] + h[2], 16);
          return `${r}, ${g}, ${b}`;
        }
        if (h.length === 6) {
          const r = parseInt(h.substring(0, 2), 16);
          const g = parseInt(h.substring(2, 4), 16);
          const b = parseInt(h.substring(4, 6), 16);
          return `${r}, ${g}, ${b}`;
        }
        return '47, 186, 173';
      };
      const colorRgb = hexToRgb(color);

      styleTag.textContent = `
        body { font-family: ${family} !important; }
        a { color: ${color} !important; }
        .btn, a.btn, button.btn, input.btn, .btn-rounded, .btn-group > .btn {
          border-radius: ${buttonBorderRadius} !important;
        }
        .btn-primary { background-color: var(--branding-theme-color) !important; border-color: var(--branding-theme-color) !important; }
        .btn-info { background-color: var(--branding-info-color) !important; border-color: var(--branding-info-border-color, var(--branding-info-color)) !important; color: var(--branding-info-on-color, #fff) !important; }
        .btn-success { background-color: var(--branding-success-color) !important; border-color: var(--branding-success-border-color, var(--branding-success-color)) !important; color: var(--branding-success-on-color, #fff) !important; }
        .btn-warning { background-color: var(--branding-warning-color) !important; border-color: var(--branding-warning-border-color, var(--branding-warning-color)) !important; color: var(--branding-warning-on-color, #fff) !important; }
        .btn-danger { background-color: var(--branding-danger-color) !important; border-color: var(--branding-danger-border-color, var(--branding-danger-color)) !important; color: var(--branding-danger-on-color, #fff) !important; }
        .btn-scooter { background-color: var(--branding-scooter-color) !important; border-color: var(--branding-scooter-border-color, var(--branding-scooter-color)) !important; color: var(--branding-scooter-on-color, #fff) !important; }
        .btn-outline-primary { color: ${color} !important; border-color: ${color} !important; }
        .btn-outline-info { color: var(--branding-info-foreground-color, var(--branding-info-color)) !important; border-color: var(--branding-info-border-color, var(--branding-info-color)) !important; }
        .btn-outline-success { color: var(--branding-success-foreground-color, var(--branding-success-color)) !important; border-color: var(--branding-success-border-color, var(--branding-success-color)) !important; }
        .btn-outline-warning { color: var(--branding-warning-foreground-color, var(--branding-warning-color)) !important; border-color: var(--branding-warning-border-color, var(--branding-warning-color)) !important; }
        .btn-outline-danger { color: var(--branding-danger-foreground-color, var(--branding-danger-color)) !important; border-color: var(--branding-danger-border-color, var(--branding-danger-color)) !important; }
        .btn-outline-scooter { color: var(--branding-scooter-foreground-color, var(--branding-scooter-color)) !important; border-color: var(--branding-scooter-border-color, var(--branding-scooter-color)) !important; }
        .btn-outline-primary:hover { background-color: ${color} !important; color: #ffffff !important; border-color: ${color} !important; }
        .btn-outline-info:hover { background-color: var(--branding-info-color) !important; color: var(--branding-info-on-color, #fff) !important; }
        .btn-outline-success:hover { background-color: var(--branding-success-color) !important; color: var(--branding-success-on-color, #fff) !important; }
        .btn-outline-warning:hover { background-color: var(--branding-warning-color) !important; color: var(--branding-warning-on-color, #fff) !important; }
        .btn-outline-danger:hover { background-color: var(--branding-danger-color) !important; color: var(--branding-danger-on-color, #fff) !important; }
        .btn-outline-scooter:hover { background-color: var(--branding-scooter-color) !important; color: var(--branding-scooter-on-color, #fff) !important; }
        .text-mmddarkblue { color: ${color} !important; }
        .text-info { color: var(--branding-info-foreground-color, var(--branding-info-color)) !important; }
        .text-success { color: var(--branding-success-foreground-color, var(--branding-success-color)) !important; }
        .text-warning { color: var(--branding-warning-foreground-color, var(--branding-warning-color)) !important; }
        .text-danger { color: var(--branding-danger-foreground-color, var(--branding-danger-color)) !important; }
        .text-scooter { color: var(--branding-scooter-foreground-color, var(--branding-scooter-color)) !important; }
        .navbarbg, .topbar, [data-navbarbg], [data-logobg] { background-color: ${color} !important; }
        /* Topbar chrome only — not dropdown menus */
        .topbar .navbar-header .nav-toggler,
        .topbar .navbar-header .topbartoggler,
        .topbar .top-navbar > .navbar-collapse > .navbar-nav > .nav-item > .nav-link,
        .topbar .top-navbar > .navbar-collapse > .navbar-nav > .nav-item > .nav-link i,
        .topbar .top-navbar > .navbar-collapse > .navbar-nav > .nav-item > .dropdown > .nav-link,
        .topbar .top-navbar > .navbar-collapse > .navbar-nav > .nav-item > .dropdown > .nav-link i {
          color: var(--branding-topbar-text-muted) !important;
        }
        .topbar .dropdown-menu .dropdown-item,
        .topbar .dropdown-menu .dropdown-item a,
        .topbar .dropdown-menu .dropdown-item i,
        .topbar .dropdown-menu .dropdown-item .mdi,
        .topbar .dropdown-menu .dropdown-item [class^="ti-"],
        .topbar .dropdown-menu .dropdown-item .fa {
          color: var(--branding-theme-color) !important;
        }
        /* Sidebar — match runtimeBranding.css (override compiled skin backgrounds) */
        #main-wrapper .left-sidebar,
        #main-wrapper .left-sidebar[data-sidebarbg],
        #main-wrapper .left-sidebar[data-sidebarbg] ul,
        .left-sidebar,
        .left-sidebar .scroll-sidebar,
        .left-sidebar .sidebar-nav,
        .left-sidebar .sidebar-nav ul,
        #sidebarbg,
        #sidebarbg .scroll-sidebar,
        #sidebarbg .sidebar-nav,
        #sidebarbg .sidebar-nav ul {
          background: var(--branding-sidebar-color) !important;
        }
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link i,
        .left-sidebar .sidebar-nav ul .nav-small-cap,
        .left-sidebar .sidebar-nav ul .nav-small-cap i,
        #sidebarbg .sidebar-nav ul .sidebar-item .sidebar-link,
        #sidebarbg .sidebar-nav ul .sidebar-item .sidebar-link i,
        #sidebarbg .sidebar-nav ul .nav-small-cap,
        #sidebarbg .sidebar-nav ul .nav-small-cap i {
          color: var(--branding-sidebar-text-muted) !important;
          opacity: 1 !important;
        }
        .left-sidebar .sidebar-nav .has-arrow::after,
        #sidebarbg .sidebar-nav .has-arrow::after {
          border-color: var(--branding-sidebar-text-muted) !important;
        }
        .left-sidebar .sidebar-nav ul .sidebar-item.selected > .sidebar-link,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link.active,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link:hover,
        .left-sidebar .sidebar-nav ul .sidebar-item .first-level .sidebar-link.active,
        .left-sidebar .sidebar-nav ul .sidebar-item .second-level .sidebar-link.active,
        #sidebarbg .sidebar-nav ul .sidebar-item.selected > .sidebar-link,
        #sidebarbg .sidebar-nav ul .sidebar-item .sidebar-link.active,
        #sidebarbg .sidebar-nav ul .sidebar-item .sidebar-link:hover {
          background: var(--branding-sidebar-hover-bg) !important;
          border-color: var(--branding-sidebar-hover-border) !important;
          color: var(--branding-sidebar-hover-text-color) !important;
        }
        .left-sidebar .sidebar-nav ul .sidebar-item.selected > .sidebar-link i,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link.active i,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link:hover i,
        .left-sidebar .sidebar-nav ul .sidebar-item.selected > .sidebar-link .hide-menu,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link.active .hide-menu,
        .left-sidebar .sidebar-nav ul .sidebar-item .sidebar-link:hover .hide-menu,
        #sidebarbg .sidebar-nav ul .sidebar-item.selected > .sidebar-link i,
        #sidebarbg .sidebar-nav ul .sidebar-item .sidebar-link.active i,
        #sidebarbg .sidebar-nav ul .sidebar-item .sidebar-link:hover i {
          color: var(--branding-sidebar-hover-text-color) !important;
        }
        /* Table and badges */
        .badge-primary { background-color: ${color} !important; }
        .badge-info { background-color: ${info} !important; }
        .badge-success { background-color: ${success} !important; }
        .badge-warning { background-color: ${warning} !important; }
        .badge-danger { background-color: ${danger} !important; }
        .badge-scooter { background-color: ${scooter} !important; }
        .page-breadcrumb .breadcrumb .breadcrumb-item a { color: ${color} !important; }
        .page-wrapper .page-content, .card .card-title { font-family: ${family} !important; }
        /* Pagination */
        .pagination .page-link { color: ${color} !important; border-color: rgba(${colorRgb}, 0.3) !important; }
        .pagination .page-link:hover { color: ${color} !important; }
        .pagination .page-item.active .page-link { background-color: ${color} !important; border-color: ${color} !important; color: #ffffff !important; }
        /* Forms: checkbox/radio/focus */
        .form-check-input:checked { background-color: ${color} !important; border-color: ${color} !important; }
        .form-check-input:focus { border-color: rgba(${colorRgb}, 0.5) !important; box-shadow: 0 0 0 .2rem rgba(${colorRgb}, .25) !important; }
        .custom-control-input:checked ~ .custom-control-label::before { background-color: ${color} !important; border-color: ${color} !important; }
        .custom-control-input:focus ~ .custom-control-label::before { box-shadow: 0 0 0 .2rem rgba(${colorRgb}, .25) !important; border-color: rgba(${colorRgb}, .5) !important; }
        .form-control:focus { border-color: rgba(${colorRgb}, 0.5) !important; box-shadow: 0 0 0 .2rem rgba(${colorRgb}, .25) !important; }
        /* Spinner */
        .spinner-border, .spinner-grow { color: ${color} !important; }
        .spinner-border { border-top-color: ${color} !important; }
        .releaseSpinner, .tableHelper .spinner-border, .tableHelper .spinner-grow { color: ${color} !important; border-top-color: ${color} !important; }
        /* Table helper text */
        .tableHelper p { color: ${color} !important; }
        /* About menu profile button (mmdelectricpurple maps to skin) */
        .btn-mmdelectricpurple, .btn-mmddarkblue, .btn-mmdlitepurple { background-color: ${color} !important; border-color: ${color} !important; }
        .btn-mmdelectricpurple:hover, .btn-mmddarkblue:hover, .btn-mmdlitepurple:hover { background-color: ${color} !important; border-color: ${color} !important; opacity: .9; }
        ${whitePrimary ? `
        a { color: #000000 !important; }
        .btn-primary, .btn-mmdelectricpurple, .btn-mmddarkblue, .btn-mmdlitepurple { color: #000000 !important; border-color: #000000 !important; }
        .btn-outline-primary { color: #000000 !important; border-color: #000000 !important; }
        .btn-outline-primary:hover { color: #000000 !important; border-color: #000000 !important; background-color: rgba(0, 0, 0, 0.08) !important; }
        .text-mmddarkblue, .tableHelper p, .page-breadcrumb .breadcrumb .breadcrumb-item a { color: #000000 !important; }
        .pagination .page-link, .pagination .page-link:hover { color: #000000 !important; }
        .pagination .page-item.active .page-link { color: #000000 !important; border-color: #000000 !important; }
        .topbar .dropdown-menu .dropdown-item, .topbar .dropdown-menu .dropdown-item a, .topbar .dropdown-menu .dropdown-item i, .topbar .dropdown-menu .dropdown-item .fa { color: #000000 !important; }
        ` : ''}
        .user-dd .with-arrow > span { background-color: ${color} !important; }
        /* Hide Zapier interfaces chatbot embed only inside preview */
        zapier-interfaces-chatbot-embed,
        #zapier-interfaces-chatbot-embed,
        .zapier-interfaces-chatbot-embed,
        [id*="zapier-interfaces-chatbot-embed"] {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;

      // Swap logo if available
      if (logoUrl) {
        const imgs = doc.getElementsByClassName('mmLogo');
        if (imgs && imgs.length > 0) {
          Array.from(imgs).forEach((img) => {
            img.setAttribute('src', logoUrl);
            img.style.objectFit = 'contain';
          });
        }
      }

      // Update footer company name/website and policy links if provided
      try {
        const footer = doc.querySelector('footer.footer');
        if (footer) {
          const links = footer.getElementsByTagName('a');
          if (links && links.length > 0) {
            const companyAnchor = links[0];
            if (companyName && companyName.trim()) {
              companyAnchor.textContent = companyName + ' ';
            }
            if (companyWebsite && companyWebsite.trim()) {
              companyAnchor.setAttribute('href', companyWebsite);
            }
            const anchorsArr = Array.from(links);
            const privacyAnchor = anchorsArr.find(a => (a.textContent || '').toLowerCase().includes('privacy')) || anchorsArr[1];
            const termsAnchor = anchorsArr.find(a => (a.textContent || '').toLowerCase().includes('terms')) || anchorsArr[2];
            if (privacyPolicyUrl && privacyAnchor) {
              privacyAnchor.setAttribute('href', privacyPolicyUrl);
            }
            if (termsAndConditionsUrl && termsAnchor) {
              termsAnchor.setAttribute('href', termsAndConditionsUrl);
            }
          }
        }
      } catch (_) {}

      // Make preview non-interactive to avoid affecting user session (e.g., logout)
      const blockEvent = (e) => {
        try {
          const target = e.target;
          if (!target) return;
          const interactive = target.closest && target.closest('a, button, .btn, [role="button"], input[type="submit"], .nav-link, .sidebar-link, .dropdown-item');
          if (interactive) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            return false;
          }
        } catch (_) {}
      };

      doc.removeEventListener('click', blockEvent, true);
      doc.addEventListener('click', blockEvent, true);
      doc.removeEventListener('submit', blockEvent, true);
      doc.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        return false;
      }, true);

      // Proactively hide Zapier chatbot and related iframes as they are injected
      const hide3p = () => {
        try {
          const sel = [
            'zapier-interfaces-chatbot-embed',
            '#zapier-interfaces-chatbot-embed',
            '.zapier-interfaces-chatbot-embed',
            '[id*="zapier-interfaces-chatbot-embed"]',
          ].join(',');
          const nodes = doc.querySelectorAll(sel);
          nodes.forEach((n) => {
            n.style.setProperty('display', 'none', 'important');
            n.style.setProperty('visibility', 'hidden', 'important');
            n.style.setProperty('pointer-events', 'none', 'important');
          });
        } catch (_) {}
      };
      hide3p();
      if (doc.__brandingPreviewObserver) {
        try { doc.__brandingPreviewObserver.disconnect(); } catch (_) {}
      }
      const mo = new MutationObserver(() => hide3p());
      mo.observe(doc.body || doc.documentElement, { childList: true, subtree: true });
      doc.__brandingPreviewObserver = mo;
    } catch (e) {
      // noop
    }
  }, [brandColor, sidebarColor, infoColor, successColor, warningColor, dangerColor, scooterColor, buttonStyle, fontFamily, logoUrl, companyName, companyWebsite, privacyPolicyUrl, termsAndConditionsUrl]);

  const scheduleInjectBranding = useCallback(() => {
    reapplyTimersRef.current.forEach(clearTimeout);
    reapplyTimersRef.current = PREVIEW_REAPPLY_DELAYS_MS.map(delay =>
      setTimeout(injectBranding, delay),
    );
  }, [injectBranding]);

  useEffect(() => {
    scheduleInjectBranding();
    return () => {
      reapplyTimersRef.current.forEach(clearTimeout);
      reapplyTimersRef.current = [];
    };
  }, [scheduleInjectBranding]);

  return (
    <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', overflow: 'hidden' }}>
      <iframe
        ref={iframeRef}
        title="Branding Preview"
        src="/"
        style={{ width: '100%', height: `${height}px`, border: '0' }}
        onLoad={scheduleInjectBranding}
        sandbox="allow-same-origin allow-scripts"
      />
    </div>
  );
};

export default BrandingPreview;


